import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import GuestProjectsSelect from '../../../../shared/components/guest-projects-select/GuestProjectsSelect';

interface GuestScopeDialogProps {
  visible: boolean;
  /** Scope at opening; the dialog edits a copy and hands the result back on Save. */
  initialIds: number[];
  onSave: (projectIds: number[]) => void;
  onHide: () => void;
  /** Save in flight (the button shows it and stays put). */
  saving?: boolean;
  /** Why the last save failed; shown under the select. */
  error?: string | null;
}

/**
 * The Team page's "Edit guest projects" dialog as a component: the same shell, the shared project
 * select (one rule of what a guest may be given) and the same empty-scope hint. The result goes to
 * the parent: for an existing guest the Team section saves it (`PUT /team/data/:id/projects`), on the
 * role → guest path it is still applied locally until the role endpoint exists.
 */
const GuestScopeDialog: React.FC<GuestScopeDialogProps> = ({
  visible,
  initialIds,
  onSave,
  onHide,
  saving = false,
  error = null,
}) => {
  const { t } = useTranslation();
  const [ids, setIds] = useState<number[]>(initialIds);

  useEffect(() => {
    if (visible) setIds(initialIds);
  }, [visible, initialIds]);

  return (
    <Dialog
      className="dialog-box"
      header={t('guestEditProjects')}
      visible={visible}
      draggable={false}
      onHide={onHide}
    >
      <div className="agency-hub-team__guest-scope">
        <div className="form__label">{t('guestProjectsAccess')}:</div>
        <GuestProjectsSelect value={ids} onChange={setIds} />
        {ids.length === 0 && <span>{t('guestNeedsProject')}</span>}
        {error && <span className="agency-hub-team__guest-scope-error">{error}</span>}
        <Button
          className="btn btn--sm"
          label={t('save')}
          disabled={ids.length === 0 || saving}
          loading={saving}
          onClick={() => onSave(ids)}
        />
      </div>
    </Dialog>
  );
};

export default GuestScopeDialog;
